import type { ReactNode } from "react";
import { TriangleAlert } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { buttonChrome } from "@/components/ui/button";
import { IconTile } from "@/components/ui/IconTile";
import { Panel } from "@/components/ui/Panel";

/**
 * The explicit step before something is taken away. Open while there is a
 * subject; dismissed any other way than the action, it cancels. A step that
 * asks for an answer, such as a reason, draws its field as the body and holds
 * the action closed until the answer is complete.
 *
 * A step that takes away something the attorney may miss can `warn`, with the
 * warning mark over the title, and say in a `note` what goes with it, in the
 * dialog's tone, as the design's delete dialogs do.
 */
export function ConfirmDialog({
  open,
  title,
  description,
  actionLabel,
  cancelLabel = "Cancel",
  destructive = false,
  actionDisabled = false,
  warn = false,
  note,
  holdsOpen = false,
  children,
  onCancel,
  onConfirm,
}: {
  open: boolean;
  title: string;
  description: ReactNode;
  actionLabel: string;
  /** The words on the way out, when "Cancel" is not what declining means. */
  cancelLabel?: string;
  destructive?: boolean;
  /** Closed while the answer the body asks for is not complete. */
  actionDisabled?: boolean;
  /** The warning mark over the title. */
  warn?: boolean;
  /** What goes with the act, in a box in the dialog's tone before the acts. */
  note?: ReactNode;
  /**
   * The act's answer closes the dialog, not the press: for a step whose typed words must
   * survive a refusal, the caller closes it once the server has taken them.
   */
  holdsOpen?: boolean;
  /** A field the step asks the attorney to fill, drawn between the description and the acts. */
  children?: ReactNode;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <AlertDialog open={open} onOpenChange={(next) => !next && onCancel()}>
      <AlertDialogContent>
        <AlertDialogHeader>
          {warn && (
            <IconTile tone="destructive" size="md">
              <TriangleAlert />
            </IconTile>
          )}
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>
        {children}
        {note !== undefined && (
          <Panel
            shape="row"
            tone={destructive ? "destructive" : "info"}
            className="text-[12px] leading-relaxed"
          >
            {note}
          </Panel>
        )}
        <AlertDialogFooter>
          <AlertDialogCancel>{cancelLabel}</AlertDialogCancel>
          <AlertDialogAction
            onClick={(event) => {
              if (holdsOpen) event.preventDefault();
              onConfirm();
            }}
            disabled={actionDisabled}
            className={buttonChrome({
              variant: destructive ? "danger" : "primary",
            })}
          >
            {actionLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
